import * as React from "react";
import * as Recharts from "recharts";
import { cn } from "@/lib/utils";

const THEMES = { light: "", dark: ".dark" } as const;
export type ChartConfig = Record<string, { label?: React.ReactNode; color?: string; theme?: Record<keyof typeof THEMES, string> }>;
const ChartContext = React.createContext<ChartConfig | null>(null);

function useChart() {
  const config = React.useContext(ChartContext);
  if (!config) throw new Error("Chart components require ChartContainer");
  return config;
}

export function ChartContainer({ id, className, children, config, ...props }: React.ComponentProps<"div"> & { config: ChartConfig }) {
  const generatedId = React.useId().replace(/:/g, "");
  const chartId = `chart-${id ?? generatedId}`;
  return <ChartContext.Provider value={config}>
    <div data-chart={chartId} className={cn("flex justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line]:stroke-border", className)} {...props}>
      <ChartStyle id={chartId} config={config} />
      <Recharts.ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>{children}</Recharts.ResponsiveContainer>
    </div>
  </ChartContext.Provider>;
}

export function ChartStyle({ id, config }: { id: string; config: ChartConfig }) {
  const rules = Object.entries(THEMES).map(([theme, prefix]) => {
    const variables = Object.entries(config).map(([key, item]) => {
      const color = item.theme?.[theme as keyof typeof THEMES] ?? item.color;
      return color && /^[a-zA-Z0-9_-]+$/.test(key) ? `--color-${key}:${color};` : "";
    }).join("");
    return `${prefix} [data-chart="${id}"]{${variables}}`;
  }).join("\n");
  return <style dangerouslySetInnerHTML={{ __html: rules }} />;
}

export const ChartTooltip = Recharts.Tooltip;
export const ChartLegend = Recharts.Legend;

type PayloadItem = { dataKey?: string | number; name?: string | number; value?: string | number; color?: string; payload?: Record<string, unknown> };
type TooltipContentProps = {
  active?: boolean; payload?: PayloadItem[]; label?: string | number;
  className?: string; hideLabel?: boolean; hideIndicator?: boolean;
  indicator?: "dot" | "line" | "dashed"; labelFormatter?: (label: string | number) => React.ReactNode;
  formatter?: (value: string | number, name: string | number) => React.ReactNode;
};

export function ChartTooltipContent({ active, payload, label, className, hideLabel, hideIndicator, indicator = "dot", labelFormatter, formatter }: TooltipContentProps) {
  const config = useChart();
  if (!active || !payload?.length) return null;
  return <div className={cn("grid min-w-[8rem] gap-1 rounded-lg border bg-background px-3 py-2 text-xs shadow-xl", className)}>
    {!hideLabel && label != null && <div className="font-medium">{labelFormatter ? labelFormatter(label) : label}</div>}
    {payload.map((item, index) => {
      const key = String(item.dataKey ?? item.name ?? index);
      const name = config[key]?.label ?? item.name ?? key;
      return <div key={key} className="flex items-center gap-2">
        {!hideIndicator && <span className={cn("inline-block size-2 shrink-0 rounded-full", indicator === "line" && "h-0.5 rounded-none", indicator === "dashed" && "h-0.5 border-t border-dashed bg-transparent")} style={{ backgroundColor: item.color ?? config[key]?.color }} />}
        <span className="text-muted-foreground">{name}</span>
        <span className="ml-auto font-mono font-medium tabular-nums">{formatter ? formatter(item.value ?? 0, item.name ?? key) : item.value}</span>
      </div>;
    })}
  </div>;
}

export function ChartLegendContent({ payload, className }: { payload?: Array<{ dataKey?: string | number; value?: string | number; color?: string }>; className?: string }) {
  const config = useChart();
  if (!payload?.length) return null;
  return <div className={cn("flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2", className)}>
    {payload.map((item, index) => {
      const key = String(item.dataKey ?? item.value ?? index);
      return <div key={key} className="flex items-center gap-1.5"><span className="size-2 rounded-sm" style={{ backgroundColor: item.color ?? config[key]?.color }} />{config[key]?.label ?? item.value ?? key}</div>;
    })}
  </div>;
}
